import { ReactNode } from 'react';

/* Steps */
export const STEP_LOGIN = 'STEP_LOGIN';
export const STEP_AUTH_ERROR = 'STEP_AUTH_ERROR';
export const STEP_FLOW = 'STEP_FLOW';
export const STEP_PROMO_TOKEN_ADD_USER_TO_WHITELIST = 'STEP_PROMO_TOKEN_ADD_USER_TO_WHITELIST';
export const STEP_PROMO_TOKEN_PROMO_CODE = 'STEP_PROMO_TOKEN_PROMO_CODE';
export const STEP_PROMO_TOKEN_NO_PROMO_CODE = 'STEP_PROMO_TOKEN_NO_PROMO_CODE';
export const STEP_NO_PROMO_TOKEN = 'STEP_NO_PROMO_TOKEN';
export const STEP_FORM_SUBMITTED = 'STEP_FORM_SUBMITTED';

export type Step =
    | typeof STEP_LOGIN
    | typeof STEP_AUTH_ERROR
    | typeof STEP_FLOW
    | typeof STEP_PROMO_TOKEN_ADD_USER_TO_WHITELIST
    | typeof STEP_PROMO_TOKEN_PROMO_CODE
    | typeof STEP_PROMO_TOKEN_NO_PROMO_CODE
    | typeof STEP_NO_PROMO_TOKEN
    | typeof STEP_FORM_SUBMITTED;

/* Form Data */
export const USER_ID = 'userId';
export const PRIMARY_INTEREST = 'primaryInterest';
export const PRIVATE_ADVISOR_ASSISTANCE = 'privateAdvisorAssistance';
export const PRICE_RANGE = 'priceRange';
export const RECEIVE_SMS_UPDATES = 'receiveSmsUpdates';
export const SMS_PHONE_NUMBER = 'smsPhoneNumber';
export const RECEIVE_WHATS_APP_UPDATES = 'receiveWhatsAppUpdates';
export const WHATS_APP_PHONE_NUMBER = 'whatsAppPhoneNumber';

export type FormField =
    | typeof USER_ID
    | typeof PRIMARY_INTEREST
    | typeof PRIVATE_ADVISOR_ASSISTANCE
    | typeof PRICE_RANGE
    | typeof RECEIVE_SMS_UPDATES
    | typeof SMS_PHONE_NUMBER
    | typeof RECEIVE_WHATS_APP_UPDATES
    | typeof WHATS_APP_PHONE_NUMBER;

type BooleanFormField = typeof RECEIVE_SMS_UPDATES | typeof RECEIVE_WHATS_APP_UPDATES;
type StringFormField = Exclude<FormField, BooleanFormField>;

export type FormData = {
    [fieldName in StringFormField]: string;
} & {
    [fieldName in BooleanFormField]: boolean;
};

type ErrorMessages = { [fieldName in FormField]?: ReactNode };

/* Reducer */
export const SET_STEP = 'SET_STEP';
export const SET_PROMO_CODE = 'SET_PROMO_CODE';
export const SET_FORM_DATA = 'SET_FORM_DATA';
export const SET_ERROR_MESSAGES = 'SET_ERROR_MESSAGES';
export const SET_SERVICE_ERROR = 'SET_SERVICE_ERROR';
export const SET_FORM_SUBMITTED = 'SET_FORM_SUBMITTED';
export const SET_LOADING = 'SET_LOADING';

type ActionType =
    | {
          type: typeof SET_STEP;
          step: Step;
      }
    | {
          type: typeof SET_PROMO_CODE;
          promoCode: string;
      }
    | {
          type: typeof SET_FORM_DATA;
          formData: Partial<FormData>;
      }
    | {
          type: typeof SET_ERROR_MESSAGES;
          errorMessages: ErrorMessages;
      }
    | {
          type: typeof SET_SERVICE_ERROR;
          hasServiceError: boolean;
      }
    | {
          type: typeof SET_LOADING;
          isLoading: boolean;
      };

export type ReducerState = {
    step: Step;
    promoCode: string;
    formData: FormData;
    errorMessages: ErrorMessages;
    hasServiceError: boolean;
    isLoading: boolean;
};

export type DispatchType = React.Dispatch<ActionType>;

export const initialState: ReducerState = {
    step: STEP_LOGIN,
    promoCode: '',
    formData: {
        userId: '',
        primaryInterest: '',
        privateAdvisorAssistance: '',
        priceRange: '',
        receiveSmsUpdates: false,
        smsPhoneNumber: '',
        receiveWhatsAppUpdates: false,
        whatsAppPhoneNumber: '',
    },
    errorMessages: {},
    hasServiceError: false,
    isLoading: false,
};

export function reducer(state: ReducerState, action: ActionType): ReducerState {
    switch (action.type) {
        case SET_STEP: {
            return { ...state, step: action.step };
        }

        case SET_PROMO_CODE: {
            return { ...state, promoCode: action.promoCode };
        }

        case SET_FORM_DATA: {
            return {
                ...state,
                formData: {
                    ...state.formData,
                    ...action.formData,
                },
            };
        }

        case SET_ERROR_MESSAGES: {
            return {
                ...state,
                errorMessages: {
                    ...state.errorMessages,
                    ...action.errorMessages,
                },
            };
        }

        case SET_SERVICE_ERROR: {
            return {
                ...state,
                hasServiceError: action.hasServiceError,
            };
        }

        case SET_LOADING: {
            return {
                ...state,
                isLoading: action.isLoading,
            };
        }

        default: {
            return state;
        }
    }
}
